import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { CounterNumberInput, FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { useIsMobile } from '@modernloop/shared/hooks';
import { InfoIcon } from '@modernloop/shared/icons';
import { Box, Container, Paper, Stack, Switch, Typography } from '@mui/material';
import { isUndefined } from 'lodash';

import {
  ConfigurableSelfScheduleOptions_InterviewMeetingLocationFragment,
  EmployeeByIdsQuery,
  InterviewMeetingLocationSettingsInput,
  NotificationPreferenceInput,
  SelfScheduleOptionsSettingInput,
} from 'src/generated/mloop-graphql';

import useEqualSizeSxProps from 'src/components/utils/useEqualSizeSxProps';

import { MAX_TIMEFRAME_DAYS, MIN_TIMEFRAME_DAYS } from 'src/entities/SchedulingRequestSettings/Timeframe/TimeframeDays';

import SelfSchedulePreferences, { SelfScheduleSelectedTemplates } from './SelfSchedulePreferences';
import TimeFrame, { NumberOfDays } from './TimeframePicker';

type Fragments = {
  interviewMeetingLocation: ConfigurableSelfScheduleOptions_InterviewMeetingLocationFragment | undefined;
};

interface Props {
  title?: string;
  selectedDatesOptionsCount?: number;
  numberOfDays: number | undefined;
  /** The day count the counter shows. It holds the last value while hand-picked dates are in play. */
  timeframeDays: number;
  alertJsx?: JSX.Element | boolean;
  optionsCalendarJsx?: JSX.Element;
  options: SelfScheduleOptionsSettingInput;
  templates: SelfScheduleSelectedTemplates;
  defaultTemplates: SelfScheduleSelectedTemplates;
  employeeMissingZoomUserIds?: string[];
  missingEmployeesData?: EmployeeByIdsQuery;
  onNoteChanged: (note: string) => void;
  onRollingDaysToggle?: (value: boolean) => void;
  onRollingDaysChange: (value: number) => void;
  onTimeframeDaysChange: (days: number) => void;
  onTemplatesChanged: (value: SelfScheduleSelectedTemplates) => void;
  onOptionsChanged: (value: SelfScheduleOptionsSettingInput) => void;
  candidateEventAdditionalAttendees: NotificationPreferenceInput | null;
  interviewerEventAdditionalAttendees: NotificationPreferenceInput | null;
  onCandidateEventAdditionalAttendeesChanged?: (attendees: NotificationPreferenceInput) => void;
  onInterviewerEventAdditionalAttendeesChanged?: (attendees: NotificationPreferenceInput) => void;

  shouldSendPersonalizedEmail?: boolean;
  setShouldSendPersonalizedEmail?: (val: boolean) => void;
  onInterviewMeetingLocationChanged: (interviewMeetingLocation: InterviewMeetingLocationSettingsInput) => void;
}

export const ConfigurableSelfScheduleOptions: FCWithFragments<Fragments, Props> = ({
  interviewMeetingLocation,
  title,
  selectedDatesOptionsCount,
  numberOfDays,
  timeframeDays,
  alertJsx,
  optionsCalendarJsx,
  options,
  templates,
  defaultTemplates,
  missingEmployeesData,
  employeeMissingZoomUserIds,
  candidateEventAdditionalAttendees,
  interviewerEventAdditionalAttendees,
  onRollingDaysToggle,
  onRollingDaysChange,
  onTimeframeDaysChange,
  onTemplatesChanged,
  onOptionsChanged,
  onCandidateEventAdditionalAttendeesChanged,
  onInterviewerEventAdditionalAttendeesChanged,
  shouldSendPersonalizedEmail,
  setShouldSendPersonalizedEmail,
  onNoteChanged,
  onInterviewMeetingLocationChanged,
}) => {
  const { t } = useTranslation();
  const equalSizeSxProps = useEqualSizeSxProps();
  const isFullScreen = useIsMobile();
  const daysInputEnabled = useFlag('org_self_schedule_timeframe_in_days');

  return (
    <>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          width: '100%',
        }}
      >
        <Stack spacing={1} useFlexGap>
          <Typography fontWeight={600}>
            {t('hiring-dashboard-schedule:schedule.selfScheduleOptions.whenInterviewShouldHappen')}
          </Typography>

          <Stack
            alignItems="center"
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            spacing={1}
            sx={equalSizeSxProps.equalSizeChild}
          >
            <Typography>
              {daysInputEnabled
                ? t('hiring-dashboard-schedule:schedule.requestSettings.timeframeDaysLabel')
                : t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.timeframe')}
            </Typography>
            {daysInputEnabled ? (
              // The equal-size rule gives the control half of the row, so keep the counter at the
              // right edge, in line with the switch below it.
              <Box
                data-testid="scheduling-request-timeframe-days-counter"
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <CounterNumberInput
                  value={timeframeDays}
                  min={MIN_TIMEFRAME_DAYS}
                  max={MAX_TIMEFRAME_DAYS}
                  onChange={onTimeframeDaysChange}
                />
              </Box>
            ) : (
              <TimeFrame timeFrame={numberOfDays || NumberOfDays.TwoWeeks} onChange={onRollingDaysChange} />
            )}
          </Stack>

          {onRollingDaysToggle && (
            // A plain Stack replaces a FormControlLabel here, because the theme gives a start-placed
            // label a negative margin that indents this row against the Timeframe row above it.
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
              <Typography>
                {t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.useRollingWindow')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  size="small"
                  checked={Boolean(options.rollingDays)}
                  inputProps={{
                    'aria-label': t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.useRollingWindow'),
                  }}
                  onChange={(event) => {
                    onRollingDaysToggle(event.target.checked);
                  }}
                />
                <InfoIcon
                  tooltip={t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.rollingWindowTooltip')}
                />
              </Stack>
            </Stack>
          )}
        </Stack>

        {!isUndefined(selectedDatesOptionsCount) && (
          <>
            {!isFullScreen && optionsCalendarJsx && (
              <Container
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    padding: 1.5,
                  }}
                >
                  {optionsCalendarJsx}
                </Paper>
              </Container>
            )}
            {isFullScreen && optionsCalendarJsx && <Box sx={{ margin: '0 auto' }}>{optionsCalendarJsx}</Box>}
          </>
        )}
      </Stack>

      <SelfSchedulePreferences
        alertJsx={alertJsx}
        title={title}
        onOptionsChanged={onOptionsChanged}
        options={options}
        templates={templates}
        defaultTemplates={defaultTemplates}
        missingEmployeesData={employeeMissingZoomUserIds?.length ? missingEmployeesData : undefined}
        isPersonalizedEmailChecked={Boolean(shouldSendPersonalizedEmail)}
        interviewMeetingLocation={interviewMeetingLocation}
        onTemplatesChanged={onTemplatesChanged}
        handlePersonalizedEmailCheckboxChange={(checked) => {
          setShouldSendPersonalizedEmail?.(checked);
        }}
        onNoteChanged={onNoteChanged}
        onInterviewMeetingLocationChanged={onInterviewMeetingLocationChanged}
        candidateEventAdditionalAttendees={candidateEventAdditionalAttendees}
        interviewerEventAdditionalAttendees={interviewerEventAdditionalAttendees}
        onCandidateEventAdditionalAttendeesChanged={onCandidateEventAdditionalAttendeesChanged}
        onInterviewerEventAdditionalAttendeesChanged={onInterviewerEventAdditionalAttendeesChanged}
      />
    </>
  );
};

ConfigurableSelfScheduleOptions.fragments = {
  interviewMeetingLocation: gql`
    ${SelfSchedulePreferences.fragments.interviewMeetingLocation}
    fragment ConfigurableSelfScheduleOptions_interviewMeetingLocation on InterviewMeetingLocationSettingPref {
      ...SelfSchedulePreferences_interviewMeetingLocation
    }
  `,
};
